import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  HostBinding,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core'
import {IconDefinition} from '@fortawesome/fontawesome-svg-core'
import {I18nService} from '../i18n/i18n.service'
import {BehaviorSubject, Subject} from 'rxjs'
import {debounceTime, takeUntil} from 'rxjs/operators'
import {FormControl} from '@angular/forms'
import {FilterOption, FilterOptions, FilterOptionsProvider} from './filter.types'
import {faCaretDown} from '@fortawesome/pro-solid-svg-icons/faCaretDown'
import {FilterDropdownComponent} from './filter-dropdown/filter-dropdown.component'
import {faXmark} from '@fortawesome/pro-solid-svg-icons/faXmark'

@Component({
  selector: 'cft-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterComponent implements OnInit, OnDestroy {
  icon = input<IconDefinition>()
  placeholder = input<string>()
  options = input<FilterOption<unknown>[]>()
  // A function which can be used to add options dynamically
  // for example for a search where we have a big amount of
  // options available like postal code search
  optionsProvider = input<FilterOptionsProvider>()
  defaultOption = input<FilterOption<unknown>>()
  searchDisabled = input(false)
  noOptionsMessage$ = input(this.i18n.translate$('filter.initialMessage'))

  private _value: WritableSignal<FilterOption<unknown> | undefined> = signal(undefined)
  filterChanged = output<FilterOption<unknown> | undefined>()

  @ViewChild(FilterDropdownComponent)
  protected dropdownRef!: FilterDropdownComponent

  @ViewChild('filterInput')
  protected inputRef!: ElementRef

  @HostBinding('class.focus')
  protected isFocused = false

  @HostBinding('class.active')
  protected isActive = false

  @HostBinding('class.selected')
  protected isItemSelected = !!this.defaultOption()

  private readonly destroy$ = new Subject<true>()

  protected readonly option$: BehaviorSubject<FilterOptions<unknown>> = new BehaviorSubject<FilterOptions<unknown>>({
    isLoading: false,
    options: [],
  })
  protected readonly filterControl = new FormControl('')
  protected readonly menuClose = faCaretDown
  protected readonly clearSelectiondIcon = faXmark

  constructor(
    readonly i18n: I18nService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    let previousValue: FilterOption<unknown> | undefined = undefined
    let previousDefaultOption: FilterOption<unknown> | undefined = undefined
    effect(
      () => {
        const currentValue = this._value()
        if (currentValue !== previousValue) {
          this.filterChanged.emit(currentValue)
          previousValue = currentValue
        }

        const currentDefaultOption = this.defaultOption()
        if (currentDefaultOption && currentDefaultOption !== previousDefaultOption) {
          this.selected(currentDefaultOption)
          previousDefaultOption = currentDefaultOption
        }

        if (this.options()) {
          this.option$.next({options: this.options(), isLoading: false})
        }
      },
      {allowSignalWrites: true},
    )
  }

  ngOnInit(): void {
    this.filterControl.valueChanges
      .pipe(debounceTime(this.options() ? 0 : 400), takeUntil(this.destroy$))
      .subscribe(value => {
        if (!this.searchDisabled()) {
          this.filter(value)
        }
      })
  }

  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  value() {
    return this._value()
  }

  selected(option: FilterOption<unknown>) {
    this.filterControl.setValue(option.name)
    this.isItemSelected = true
    this._value.set(option)
    this.cdr.detectChanges()
  }

  clear() {
    this.filterControl.setValue(null)
    this.isItemSelected = false
    this._value.set(undefined)
  }

  protected onFocus() {
    this.isFocused = true
  }

  protected onClick() {
    if (this.searchDisabled()) {
      this.isFocused = true
    }
    this.dropdownRef.show(true)
  }

  protected onBlur() {
    this.isFocused = false
  }

  protected filter(value: string | null) {
    if (this.options() && !this.optionsProvider()) {
      const normalize = (str: string) => str.trim().toLowerCase()
      const options = value
        ? this.options()?.filter(option => normalize(option.name).startsWith(normalize(value)))
        : this.options() || []
      return this.option$.next({
        options,
        isLoading: false,
      })
    }
    if (this.optionsProvider()) {
      this.option$.next({isLoading: true})
      this.optionsProvider()!(value ?? '')
        .pipe(takeUntil(this.destroy$))
        .subscribe(options => {
          this.option$.next(options)
        })
    }
  }

  focus() {
    this.inputRef.nativeElement.focus()
  }

  protected handleClearButton(e) {
    if (!this.isItemSelected) return
    e.stopPropagation()
    this.clear()
    this.dropdownRef.hide()
  }
}
